/**
 * @module Rail
 * Container Component Rail
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {hardwareIcKeyboardBackspace24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {getTheme} from '@ey/styled-theme';
import {useClearAllComments} from '../../../util/customHooks';
import _ from 'lodash';
import styled, {ThemeProvider} from 'styled-components';
import uuidv4 from 'uuid/v4';
import {getAccounts} from '../../../actions/Account/accountactions';
import {createIndependenceSnapshotUrl} from '../../../actions/apiUrls';
import {getContentUpdates} from '../../../actions/ContentUpdates/contentupdatesactions';
import {raiseJSValidationError} from '../../../actions/erroractions';
import {submitIndependence, submitProfile} from '../../../actions/FormData/formdataactions';
import {setIsModalOpen} from '../../../actions/setismodalopen';
import {clearFormBodySaveState} from '../../../actions/Form/formBodyActions';
import {createCanvasFormSnapshot} from '../../../actions/Task/signoffsaction';
import {anyBodyHasUnsavedChanges} from '../../../selectors/Forms/formBodySelectors';
import {getAccountBySmartEvidenceSourceId, getTaskDocumentByDocumentId} from '../../../selectors/Ares/selectors';
import {getValidationsForDocumentId} from '../../../selectors/Forms/canvasFormSelectors';
import env from '../../../util/env';
import {
	clientSideEventNames,
	documentTypes,
	engagementAccessType,
	formRoutes,
	independenceStatus,
	roles,
	serviceURL,
	smartScreenRoutes,
	validationFilter,
	validationTypes,
	ViewModes,
	WIDTH_30,
	WIDTH_50
} from '../../../util/uiconstants';
import {currentResource, labels} from '../../../util/utils';
import Button from '../../Common/BaseComponentWrappers/MotifButton';
import EllipseControl from '../../Common/EllipsesControl/EllipsesControl';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import {loadStorage, saveStorage} from '../localStorage';
import HeadersAndSections from './HeadersAndSections';
import SubmitIndependenceButton from './SubmitIndependenceButton';
import TopSection from './TopSection';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';
import ReactiveReviewModal from '../ReactiveReviewModal';
import Icon from '../../Common/BaseComponentWrappers/MotifIcon';
import Utility from '../../../util/utilityfunctions';
import {useNavigate} from 'react-router-dom';
import {useUserPreferences, useDocumentId, useEngagementId} from '../../../util/customHooks';
export default function Rail(props) {
	const {
		railData,
		selectedId,
		documentTypeId,
		formName,
		incompleteValidations,
		unresolvedValidations,
		moreMenuItems,
		independenceInfo,
		showChangesOption,
		isShared,
		itFlowValidationCount,
		itFlowFormValidations
	} = props;

	const engagementId = useEngagementId(),
		userPreferences = useUserPreferences(),
		documentId = useDocumentId(),
		navigate = useNavigate();

	const selectedHeaderIdKey = useMemo(() => `selectedHeaderId-${documentId}`, [documentId]);

	const [isExpanded, setIsExpanded] = useState(true),
		[currentHeaderId, setCurrentHeaderId] = useState(selectedId),
		[currentSectionId, setCurrentSectionId] = useState(0),
		[stateDocument, setDocument] = useState(undefined),
		[searchValue, setSearchValue] = useState(''),
		[isSearchable, setIsSearchable] = useState(false),
		[checkingForUpdates, setCheckingForUpdates] = useState(false),
		[searchedRailData, setSearchedRailData] = useState(undefined),
		[validationFilterOption, setValidationFilterOption] = useState(
			loadStorage('validationsFilter') === undefined
				? currentResource.railFilterValidations[0]
				: currentResource.railFilterValidations[loadStorage('validationsFilter')]
		),
		[showLoader, setShowLoader] = useState(false),
		[snapshotId, setSnapshotId] = useState(-1),
		[showOverlay, setShowOverlay] = useState(false),
		[hasContentUpdate, setHasContentUpdate] = useState(false),
		[page, setPage] = useState(ViewModes.Form),
		[isFormListOpen, setFormListOpenClickOutside] = useState(false),
		[showHeaderSection, setShowHeaderSection] = useState(false),
		[showDocName, setShowDocName] = useState(false),
		[nameWidth, setNameWidthDockView] = useState((window.innerWidth * WIDTH_50) / 100);

	let hasPermission =
		userPreferences && userPreferences.permissions && userPreferences.permissions.canUpdateEngagementContent;
	const tooltipMessage = !hasPermission ? labels.contentUpdateUnAuthorizedTooltipMessage : '';

	const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		signOffs = useSelector((store) => store.signOffs),
		engagementUserForIndependence = useSelector((store) => store.engagementUserForIndependence),
		submitProfileDetails = useSelector((store) => store.submitProfileDetails),
		contentUpdates = useSelector((store) => store.contentUpdates),
		validations = useSelector((store) => getValidationsForDocumentId(store, documentId)),
		engagement = useSelector((store) => store.engagement),
		quickLink = useSelector((store) => store.quickLink),
		accountName = useSelector((state) => {
			if (document && document.smartEvidenceSourceId === null) {
				const account = getAccountBySmartEvidenceSourceId(state, document.smartEvidenceSourceId);
				if (account && account.accountName) {
					return account.accountName;
				} else {
					return '';
				}
			}
		});

	if (
		hasPermission &&
		documentTypeId === documentTypes.INDEPENDENCEFORMTEMPLATE &&
		validations &&
		validations.length > 0
	) {
		hasPermission = validations.some(
			(validation) => validation.validationTypeId === validationTypes.IndependenceTemplateDifferentFromIndividual
		);
	}

	const dispatch = useDispatch();
	const {setClearAllComments, setEntityUid, setEntityId} = useClearAllComments(
		engagementId,
		documentId,
		documentTypeId,
		undefined,
		undefined,
		() => {
			setShowOverlay(false);
			setShowLoader(false);
			setClearAllComments(false);
		}
	);

	const handleFormListOpenToggle = () => {
		setFormListOpenClickOutside(!isFormListOpen);
	};

	const onExpandCollapseClick = () => {
		if (isExpanded === false) {
			setShowHeaderSection(false);
			setShowDocName(false);
		}
		setIsExpanded(!isExpanded);
		window.dispatchEvent(
			new CustomEvent(clientSideEventNames.onCollapseChange, {
				detail: {isExpanded: !isExpanded}
			})
		);

		setTimeout(() => {
			setShowHeaderSection(true);
			setShowDocName(true);
		}, 500);
		//clear search on close of rail
		onClearClick();
	};

	const railSearchData = (value) => {
		let filteredRailData = _.cloneDeep(railData);
		if (value && value.length > 0) {
			filteredRailData = filteredRailData.filter((fl) => {
				let secondLevelSearchData = secondLevelSearch(fl, value);
				if (fl.name.toLowerCase().indexOf(value) > -1) {
					if (!secondLevelSearchData) {
						let secondLevel = fl.secondLevel.map((secondLevel) => {
							let secLevel = {
								isDisplayed: secondLevel.isDisplayed,
								isCustom: secondLevel.isCustom
							};
							return secLevel;
						});
						fl.secondLevel = secondLevel;
					}
					return fl;
				} else {
					return secondLevelSearchData;
				}
			});
			setIsSearchable(true);
			setSearchedRailData(filteredRailData);
		} else {
			setIsSearchable(false);
			setSearchedRailData(undefined);
		}
	};

	const secondLevelSearch = (railData, value) => {
		let foundSecondLevel = railData.secondLevel.filter((sl) => {
			const name = sl.name;
			return (
				name.toLowerCase().indexOf(value) > -1 ||
				(sl.customLabel && sl.customLabel !== null && sl.customLabel.toLowerCase().indexOf(value) > -1)
			);
		});

		if (foundSecondLevel && foundSecondLevel.length > 0) {
			railData.secondLevel = foundSecondLevel;
		} else {
			railData = undefined;
		}
		return railData;
	};

	const onSearchChange = (event) => {
		if (event && event.currentTarget) {
			let value = event.currentTarget.value;
			setSearchValue(value);
			value = value.toLowerCase();
			railSearchData(value);
		} else {
			onClearClick();
		}
	};

	const onClearClick = (e) => {
		setSearchValue('');
		railSearchData('');
	};

	const hasUnsavedChanges = useSelector((state) => anyBodyHasUnsavedChanges(state));
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
	const unsavedChangesCallbackData = React.useRef();

	const onRailItemClick = (evt) => {
		const railItemId = evt.currentTarget?.attributes['data-id']?.value;

		if (hasUnsavedChanges) {
			unsavedChangesCallbackData.current = {
				callback: performRailChange,
				callbackParam: railItemId
			};

			evt.preventDefault();
			setShowUnsavedChangesModal(true);
		} else {
			performRailChange(railItemId);
		}
	};

	const performRailChange = (dataId) => {
		if (validationFilterOption.value === validationFilter.INCOMPLETE || validationFilter.COMMENTS) {
			props.setFilteredFormData();
		}
		window.dispatchEvent(new CustomEvent(clientSideEventNames.closeCommentsModal));
		handleRailChange(Number(dataId));
	};

	const handleRailChange = (id) => {
		let level = 'level1';
		let found = railData.some((rd) => {
			return rd.id === id;
		});
		if (!found && railData) {
			let newSelectedHeaderId = 0;
			found = railData.some((rd) => {
				if (rd.secondLevel) {
					let foundSection = rd.secondLevel.some((sl) => {
						return sl.id === id;
					});
					if (foundSection === true) {
						newSelectedHeaderId = rd.id;
						return true;
					}
				}
			});
			if (found) {
				level = 'level2';
				setCurrentHeader(newSelectedHeaderId);
				setCurrentSectionId(id);
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onRailChange, {
						detail: {id: Number(id), level: level, data: document}
					})
				);
			}
		} else {
			setCurrentHeader(id);
			setCurrentSectionId(undefined);
			window.dispatchEvent(
				new CustomEvent(clientSideEventNames.onRailChange, {
					detail: {id: undefined, level: undefined, data: undefined}
				})
			);
		}

		if (window.location.pathname === '/canvasform/details') {
			navigate(formRoutes.canvasform + window.location.search);
			setTimeout(() => {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.onRailChange, {
						detail: {id: Number(id), level: level, data: document}
					})
				);
			}, 0);
		}
	};

	const setFormListClickOutside = () => {
		setFormListOpenClickOutside(false);
	};

	const onSignOffAdded = () => {};

	const onSignOffRemoved = () => {};

	const setCurrentHeader = useCallback((headerId) => {
		setCurrentHeaderId(headerId);
		saveStorage(selectedHeaderIdKey, headerId);
	}, []);

	const onFormChange = (evt) => {
		const detail = evt.detail;
		if (detail.collapsed === true) {
			setCurrentHeader(detail.headerId);
			setCurrentSectionId(undefined);
		} else if (detail.isDeleteHeader) {
			window.dispatchEvent(
				new CustomEvent(clientSideEventNames.onRailChange, {
					detail: {isDeleteHeader: true}
				})
			);
		} else {
			setCurrentHeader(detail.headerId);

			if ((validationFilterOption.value === 1 || validationFilterOption.value === 2) && Array.isArray(railData)) {
				const header = railData.find((data) => data.id === detail.headerId);

				if (header && Array.isArray(header.secondLevel) && header.secondLevel.length > 0) {
					const section = header.secondLevel.find((data) => data.id === detail.id);

					let count = null;

					if (validationFilterOption.value === 1) {
						count =
							(section?.incompleteCount || 0) + (section?.unresolvedCount || 0) + (section?.itFlowValidationCount || 0);
					}

					if (validationFilterOption.value === 2) {
						count = section?.unresolvedCount || 0;
					}

					if (count === 0) {
						const sectionId = header.secondLevel[0].id;
						setCurrentSectionId(sectionId);
						handleRailChange(sectionId);
					} else {
						setCurrentSectionId(detail.id);
					}
				} else {
					setCurrentSectionId(detail.id);
				}
			} else {
				setCurrentSectionId(detail.id);
			}
		}
	};

	const onSnapshotChange = (event) => {
		setSnapshotId(!isNaN(event?.detail?.snapshotId) ? event?.detail?.snapshotId : -1);
	};

	const performFormViewTypeLabelClick = useCallback((value) => {
		setFormListOpenClickOutside(false);
		switch (value) {
			case ViewModes.Form:
				{
					setSnapshotId(-1);
					setPage(ViewModes.Form);
					setIsExpanded(true);
					navigate(formRoutes.canvasform + window.location.search);
					setTimeout(() => {
						setShowHeaderSection(true);
						setShowDocName(true);
						if (loadStorage('validationsFilter') !== undefined) {
							performValidationFilterChange(currentResource.railFilterValidations[loadStorage('validationsFilter')]);
						}
					}, 800);
				}
				break;
			case ViewModes.Changes:
				{
					setPage(ViewModes.Changes);
					setIsExpanded(false);
					setShowHeaderSection(false);
					setShowDocName(false);
					navigate(formRoutes.snapshot + window.location.search);
				}
				break;
			case ViewModes.Details:
				{
					setSnapshotId(-1);
					setPage(ViewModes.Details);
					setIsExpanded(false);
					setShowHeaderSection(false);
					setShowDocName(false);
					navigate(formRoutes.details + window.location.search);
				}
				break;
		}
	}, []);

	// Update selected page on the View Type dropdown -- expanded view
	const setPageTopSection = useCallback(
		(page) => {
			if (!hasUnsavedChanges) {
				setPage(page);
			}
		},
		[hasUnsavedChanges]
	);

	/* Executed when user clicks on the view type dropdown (Form / Changes / Details) */
	const onFormViewTypeLabelClick = (value, evt) => {
		if (hasUnsavedChanges && page !== value) {
			unsavedChangesCallbackData.current = {
				callback: performFormViewTypeLabelClick,
				callbackParam: value
			};

			setShowUnsavedChangesModal(true);
		} else {
			performFormViewTypeLabelClick(value);
		}
	};

	const onFormObjectClick = (e) => {
		onFormViewTypeLabelClick({currentTarget: {value: labels.form}});
		window.dispatchEvent(new CustomEvent(clientSideEventNames.onFormChange, e));
		setPage(ViewModes.Form);
		setShowHeaderSection(true);
		setTimeout(() => {
			handleRailChange(e.detail.id);
		}, 15);
	};

	const onSubmitIndependenceClick = (e) => {
		let requestURL =
			env.getBaseURL(serviceURL, engagementId) + 'InternalUsers/' + userPreferences?.id + '/IndependenceSubmit';
		setShowLoader(true);
		window.sessionStorage.removeItem('validationsFilter');
		dispatch(submitIndependence(requestURL))
			.then(() => {
				setShowLoader(false);
			})
			.catch(() => {
				setShowLoader(false);
			});
	};

	const onSubmitProfileClick = (e) => {
		dispatch(setIsModalOpen(false));
		if (hasPermission && engagement.engagementStatusId !== independenceStatus.RESTORED) {
			if (
				validations &&
				Object.keys(validations).length > 0 &&
				validations.filter((t) => t.validationTypeId === validationTypes.FinalizeIncompleteCanvasFromResponses).length >
					0
			) {
				dispatch(raiseJSValidationError(labels.submitProfileValidationErrorMessage));
				return;
			}
			setShowLoader(true);
			dispatch(submitProfile(engagementId, documentId))
				.then(() => {
					setShowLoader(false);
				})
				.catch(() => {
					setShowLoader(false);
				});
		} else {
			returnToDashboard();
		}
	};

	const returnToDashboard = () => {
		window.location.href = env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId;
	};

	const onAccountDetailsClick = () => {
		const url =
			env.getURL('hercules_web') + `${smartScreenRoutes.sem}?engagementid=${engagementId}&documentid=${documentId}`;
		window.open(url);
	};

	const setStatusLabel = (independenceStatusId) => {
		let independenceLabel = '';
		switch (independenceStatusId) {
			case independenceStatus.UNDEFINED:
				{
					independenceLabel = labels.undefined;
				}
				break;
			case independenceStatus.INCOMPLETE:
				{
					independenceLabel = labels.incomplete;
				}
				break;
			case independenceStatus.NOMATTERSIDENTIFIED:
				{
					independenceLabel = labels.noMattersIdentified;
				}
				break;
			case independenceStatus.MATTERIDENTIFIEDPENDINGACTION:
				{
					independenceLabel = labels.matterIdentifiedPendingAction;
				}
				break;
			case independenceStatus.MATTERRESOLVEDDENIEDACCESS:
				{
					independenceLabel = labels.matterResolvedDeniedAccess;
				}
				break;
			case independenceStatus.MATTERRESOLVEDGRANTEDACCESS:
				{
					independenceLabel = labels.matterResolvedGrantedAccess;
				}
				break;
			case independenceStatus.NOTAPPLICABLE:
				{
					independenceLabel = labels.notApplicable;
				}
				break;
			case independenceStatus.RESTORED:
				{
					independenceLabel = labels.restored;
				}
				break;
			case independenceStatus.OVERRIDDEN:
				{
					independenceLabel = labels.overridden;
				}
				break;
			case independenceStatus.PRIORNOMATTERSIDENTIFIED:
				{
					independenceLabel = labels.priorNoMattersIdentified;
				}
				break;
			case independenceStatus.PRIORMATTERIDENTIFIEDPENDINGACTION:
				{
					independenceLabel = labels.priorMatterIdentifiedPendingAction;
				}
				break;
			case independenceStatus.PRIORMATTERRESOLVEDGRANTEDACCESS:
				{
					independenceLabel = labels.priorMatterResolvedGrantedAccess;
				}
				break;
			case independenceStatus.PRIORMATTERRESOLVEDDENIEDACCESS:
				{
					independenceLabel = labels.priorMatterResolvedDeniedAccess;
				}
				break;
			default:
				independenceLabel = '';
		}

		return independenceLabel;
	};

	const checkForFormUpdates = () => {
		if (documentId && documentTypeId && documentTypeId !== documentTypes.PROFILEV2) {
			if (!checkingForUpdates) {
				setCheckingForUpdates(true);
				let requestURL =
					env.getBaseURL(serviceURL, engagementId) +
					'Forms?pageSize=100&currentPage=1&page=1&haveUpdates=true&documentId=' +
					documentId;
				dispatch(getContentUpdates(requestURL)).finally(() => {
					setCheckingForUpdates(false);
				});
			}
		}
	};

	const clearAllComments = (entityId, entityUid) => {
		setShowOverlay(true);
		setEntityUid(entityUid);
		setEntityId(entityId);
		setClearAllComments(true);
	};

	const onCollapseChange = (e) => {
		if (e.detail.isExpanded) {
			let width = (window.innerWidth * WIDTH_30) / 100;
			setNameWidthDockView(width);
		} else {
			let width = (window.innerWidth * WIDTH_50) / 100;
			setNameWidthDockView(width);
		}
	};

	const onBackToCCPBtnClick = () => {
		window.location.href = env.getReturnUrl();
	};

	const validationFilterChange = (option) => {
		if (hasUnsavedChanges && option.value !== validationFilter.NONE) {
			setShowUnsavedChangesModal(true);

			unsavedChangesCallbackData.current = {
				callback: performValidationFilterChange,
				callbackParam: option
			};
		} else {
			performValidationFilterChange(option);
		}
	};

	const unselectCurrentSectionId = useCallback(() => {
		setCurrentSectionId(0);
	}, []);

	const performValidationFilterChange = useCallback(
		(option) => {
			setValidationFilterOption(option);
			unselectCurrentSectionId();
			if (snapshotId === -1) {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.railFilterValidations, {
						detail: {
							filterValue: option.value,
							formData: document,
							documentTypeId: document.documentTypeId,
							signOffs: signOffs
						}
					})
				);
			}
		},
		[unselectCurrentSectionId, document, signOffs, snapshotId]
	);

	const redirectionAfterCompleteIndependence = () => {
		if (
			engagementUserForIndependence.independenceStatusId === independenceStatus.MATTERIDENTIFIEDPENDINGACTION ||
			engagementUserForIndependence.engagementAccessTypeId === engagementAccessType.INDEPENDENCEONLY
		) {
			window.location.href = env.getURL('Zeus_web');
			return;
		} else {
			window.location.href = env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId;
			return;
		}
	};

	const redirectionAfterSubmitProfile = () => {
		if (submitProfileDetails.profileActionType !== 3) {
			window.location.href = env.getURL('Zeus_web');
			return;
		} else {
			window.location.href = env.getURL('hercules_web') + '/contentupdates?engagementid=' + engagementId;
			return;
		}
	};

	const onUnsavedChangesStay = useCallback(() => {
		setShowUnsavedChangesModal(false);
		unsavedChangesCallbackData.current = undefined;
	}, []);

	const onUnsavedChangesLeave = useCallback(() => {
		dispatch(clearFormBodySaveState()).then(() => {
			const {callback, callbackParam} = unsavedChangesCallbackData.current;
			callback && callback(callbackParam);

			// 3. Hide the modal.
			setShowUnsavedChangesModal(false);
			unsavedChangesCallbackData.current = undefined;
		});
	}, [dispatch]);

	useEffect(() => {
		if (contentUpdates.formData && contentUpdates.formData.length > 0 && hasContentUpdate === false) {
			let found = contentUpdates.formData.find((cu) => {
				return cu.documentID === documentId;
			});
			if (found) {
				/*there is a content update for this form*/
				setHasContentUpdate(true);
			}
		}
	}, [contentUpdates]);

	useEffect(() => {
		if (selectedId !== undefined && selectedId > 0) {
			handleRailChange(selectedId);
		}
		onClearClick();
	}, [selectedId]);

	useEffect(() => {
		if (Object.keys(engagementUserForIndependence).length > 0) {
			let snapshotRequestURL = createIndependenceSnapshotUrl(engagementId, documentId);

			dispatch(createCanvasFormSnapshot(snapshotRequestURL, {ApprovalId: 0})).finally(() => {
				redirectionAfterCompleteIndependence();
			});
		}
	}, [dispatch, engagementUserForIndependence]);

	useEffect(() => {
		if (Object.keys(submitProfileDetails).length > 0 && submitProfileDetails.isContentUpdated) {
			let snapshotRequestURL = env.getBaseURL(serviceURL, engagementId) + 'Forms/' + documentId + '/Snapshots';

			dispatch(createCanvasFormSnapshot(snapshotRequestURL, {ApprovalId: 0})).finally(() => {
				redirectionAfterSubmitProfile();
			});
		}
	}, [dispatch, submitProfileDetails]);

	useEffect(() => {
		saveStorage(selectedHeaderIdKey, undefined);
		if (Utility.getKronosUrl() === formRoutes.canvasform.toLowerCase()) {
			setPage(ViewModes.Form); // forms
			setIsExpanded(true);
			setTimeout(() => {
				setShowHeaderSection(true);
				setShowDocName(true);
			}, 500);
		} else if (Utility.getKronosUrl() === formRoutes.snapshot.toLowerCase()) {
			setPage(ViewModes.Changes); // Changes
			setIsExpanded(false);
			setShowHeaderSection(false);
			setShowDocName(false);
		} else if (Utility.getKronosUrl() === formRoutes.details.toLowerCase()) {
			setPage(ViewModes.Details); // Details
			setIsExpanded(false);
			setShowHeaderSection(false);
			setShowDocName(false);
		}

		if (railData && railData.length > 0) {
			if (currentHeaderId === undefined || currentHeaderId <= 0) {
				if (railData[0].secondLevel?.length > 0) {
					railData.every((header) => {
						const displayedSections = header.secondLevel.filter((sl) => sl.isDisplayed || sl.isCustom);
						if (displayedSections.length > 0) {
							setCurrentSectionId(displayedSections[0].id);
							setCurrentHeader(header.id);
							setCurrentHeaderId(header.id);
							return false;
						}
						return true;
					});
				}
			}
		}

		if (documentTypeId === documentTypes.ACCOUNT_CONCLUSION && engagementId > 0 && documentId !== '') {
			dispatch(
				getAccounts(engagementId, {
					orderBy: 'accountName',
					includeAssertions: true
				})
			);
		}

		window.addEventListener(clientSideEventNames.unselectCurrentSectionId, unselectCurrentSectionId);
		window.addEventListener(clientSideEventNames.onCollapseChange, onCollapseChange);
		window.addEventListener(clientSideEventNames.onSnapshotChange, onSnapshotChange);
		window.addEventListener(clientSideEventNames.onFormObjectClick, onFormObjectClick);
		window.addEventListener(clientSideEventNames.onFormChange, onFormChange);
		return () => {
			window.removeEventListener(clientSideEventNames.unselectCurrentSectionId, unselectCurrentSectionId);
			window.removeEventListener(clientSideEventNames.onCollapseChange, onCollapseChange);
			window.removeEventListener(clientSideEventNames.onSnapshotChange, onSnapshotChange);
			window.removeEventListener(clientSideEventNames.onFormObjectClick, onFormObjectClick);
			window.removeEventListener(clientSideEventNames.onFormChange, onFormChange);
		};
	}, []);

	useEffect(() => {
		if (
			!userPreferences.isGuestUser &&
			engagement &&
			engagement.isProfileComplete !== undefined &&
			engagement.isProfileComplete !== false
		) {
			// Do not make approval and Form Update calls for external Users.
			if (!userPreferences.isGuestUser && engagement && engagement.isProfileComplete !== false) {
				if (page !== ViewModes.Changes && (document?.sourceEngagementId === null || !document?.sourceEngagementId)) {
					checkForFormUpdates();
				}
			}
		}
	}, [engagement, userPreferences, document, stateDocument]);

	useEffect(() => {
		if (document !== undefined) {
			setDocument(document);
		}
	}, [document]);

	useEffect(() => {
		if (isSearchable && searchValue) {
			railSearchData(searchValue);
		}
	}, [railData]);

	let filteredRailData = [];

	if (isSearchable) {
		filteredRailData = searchedRailData;
	} else {
		filteredRailData = railData;
	}

	let filteredMoreMenuItems = [];

	if (isShared || (userPreferences.isGuestUser && !userPreferences.isCEAPUser)) {
		filteredMoreMenuItems = moreMenuItems && moreMenuItems.filter((data) => data.label !== labels.overwriteForm);
	} else {
		filteredMoreMenuItems = moreMenuItems;
	}

	if (documentTypeId === documentTypes.PROFILEV2) {
		filteredMoreMenuItems =
			filteredMoreMenuItems &&
			filteredMoreMenuItems.filter((data) => data.label !== labels.createCustom && data.label !== labels.overwriteForm);
	}

	if (documentTypeId === documentTypes.ACCOUNT_CONCLUSION) {
		filteredMoreMenuItems =
			filteredMoreMenuItems && filteredMoreMenuItems.filter((data) => data.label !== labels.overwriteForm);
	}

	return (
		<ThemeProvider theme={getTheme()}>
			<StyledCanvasFormLeftNavWrapper
				className={`leftSideSection ${!isExpanded ? 'leftSideSectionCollapsed' : 'leftSideSectionExpanded'}`}
			>
				<LoadingIndicator show={showOverlay} styleName="ClearCommentsLoader" />
				{showLoader && (
					<>
						<LoadingIndicator view="inlineView" styleName="FullPageLoader" />
						<span className={`RailOverlay ${showLoader ? 'LoaderOverlay' : ''}`} />
					</>
				)}
				<section className={`top-section canvasFormLeftNavWrapper ${!isExpanded ? 'hideCanvasFormBorder' : ''}`}>
					<ReactiveReviewModal entityUId={documentId} />
					{stateDocument !== undefined && (
						<TopSection
							userPreferences={userPreferences}
							engagement={engagement}
							hasContentUpdate={hasContentUpdate}
							documentTypeId={documentTypeId}
							independenceInfo={independenceInfo}
							formName={formName}
							onExpandCollapseClick={onExpandCollapseClick}
							isExpanded={isExpanded}
							setStatusLabel={setStatusLabel}
							filteredMoreMenuItems={filteredMoreMenuItems}
							setIsExpanded={setIsExpanded}
							onSearchChange={onSearchChange}
							searchValue={searchValue}
							page={page}
							setPage={setPageTopSection}
							validationFilterOption={validationFilterOption}
							validationFilterChange={validationFilterChange}
							isFormListOpen={isFormListOpen}
							setFormListClickOutside={setFormListClickOutside}
							handleFormListOpenToggle={handleFormListOpenToggle}
							showChangesOption={showChangesOption}
							onFormViewLabelClick={onFormViewTypeLabelClick}
							incompleteValidations={incompleteValidations}
							unresolvedValidations={unresolvedValidations}
							itFlowValidationCount={itFlowValidationCount}
							itFlowFormValidations={itFlowFormValidations}
							validations={validations}
							clearComments={clearAllComments}
							setDocument={setDocument}
							onSignOffAdded={onSignOffAdded}
							onSignOffRemoved={onSignOffRemoved}
							document={stateDocument}
							showDocName={showDocName}
							signOffs={signOffs}
							accountName={accountName}
							documentId={documentId}
						/>
					)}
					{showHeaderSection && (
						<section className="headerSection customScrollbar">
							{page === ViewModes.Form ? (
								<>
									<section className="canvasFormLeftNavSlider" key={uuidv4()}>
										{filteredRailData && filteredRailData.length > 0 ? (
											<HeadersAndSections
												formData={document}
												signOffs={signOffs}
												filteredRailData={filteredRailData}
												currentHeaderId={currentHeaderId}
												onClickHandle={onRailItemClick}
												isExpanded={isExpanded}
												clearComments={clearAllComments}
												currentSectionId={currentSectionId}
												showValidationsIcon={!userPreferences.isGuestUser}
											/>
										) : (
											<>
												{isSearchable ? (
													<section className={`emptyContainer noresult ${!isExpanded ? 'hideContent' : ''}`}>
														{labels.noResultsFound}
													</section>
												) : (
													<section className={`emptyContainer ${!isExpanded ? 'hideContent' : ''}`}>
														{labels.noContentAvailable}
													</section>
												)}
											</>
										)}
									</section>
								</>
							) : (
								<section className="navigationUnavailable">{labels.navigationUnavailable}</section>
							)}
						</section>
					)}
					<section
						className={`inCompleteSection ${!isExpanded ? 'hide' : 'show'} ${
							documentTypeId === documentTypes.PROFILEV2 ||
							(userPreferences.isGuestUser &&
								!userPreferences.isCEAPUser &&
								env.getReturnUrl() &&
								env.getReturnUrl().length > 0)
								? 'navigationBorder'
								: ''
						}`}
					>
						{documentTypeId === documentTypes.ACCOUNT_CONCLUSION && quickLink.hasSubstantiveEvaluationMatrix && (
							<section className="accountDetailsSectionBtn">
								<Button
									onClick={onAccountDetailsClick}
									variant="primary-alt"
									showLabel
									buttonLabel={labels.accountDetails}
								/>
							</section>
						)}
						{documentTypeId === documentTypes.PROFILEV2 && (
							<section className="submitProfileBtn">
								<Button
									onClick={onSubmitProfileClick}
									variant="primary-alt"
									showLabel
									buttonLabel={
										!hasPermission || engagement.engagementStatusId === independenceStatus.RESTORED
											? labels.returnToDashboard
											: labels.submitProfile
									}
								/>
								<>
									{!hasPermission && tooltipMessage !== '' && (
										<section className="disabled-infotooltip">
											<span className="tooltip-downarrow" />
											<span>{tooltipMessage}</span>
										</section>
									)}
								</>
								{hasPermission &&
									engagement.isProfileComplete === true &&
									engagement.engagementStatusId !== independenceStatus.RESTORED && (
										<section className="returnToDashboardLink">
											<EllipseControl
												content={labels.returnToDashboard}
												tooltip={labels.returnToDashboard}
												displayId={''}
												noOfLines={1}
												onClick={returnToDashboard}
											/>
										</section>
									)}
							</section>
						)}
						{userPreferences.isGuestUser &&
							!userPreferences.isCEAPUser &&
							env.getReturnUrl() &&
							env.getReturnUrl().length > 0 && (
								<section className="CCPWrapper">
									<section className="CCPContent">
										<span className="CCPLabels">{labels.guidanceMessageBackToCCP}</span>
									</section>
									<section className="BackToCCPBtn">
										<Icon src={hardwareIcKeyboardBackspace24px} />
										<section className="returnToDashboardLink">
											<EllipseControl
												content={labels.backToCCP}
												tooltip={labels.backToCCP}
												displayId={''}
												noOfLines={1}
												onClick={onBackToCCPBtnClick}
											/>
										</section>
									</section>
								</section>
							)}
					</section>
					{documentTypeId === documentTypes.INDEPENDENCEFORMTEMPLATE && (
						<section className={`SubmitIndependenceButtonWrapper ${!isExpanded ? 'hide' : 'show'}`}>
							<SubmitIndependenceButton
								engagementId={engagementId}
								documentId={documentId}
								disabled={!hasPermission}
								props
							/>
						</section>
					)}
				</section>
				{showUnsavedChangesModal && (
					<UnsavedChangesModal
						show={showUnsavedChangesModal}
						onCancel={onUnsavedChangesStay}
						onConfirm={onUnsavedChangesLeave}
					/>
				)}
			</StyledCanvasFormLeftNavWrapper>
		</ThemeProvider>
	);
}

const StyledCanvasFormLeftNavWrapper = styled.section`
	display: inline-flex;
	height: 100%;
	background: var(--neutrals-bg-gradient1);
	border-right: var(--px-1) solid var(--neutrals-100);
	padding-left: var(--px-20) !important;
	&.leftSideSectionCollapsed {
		padding-left: 0 !important;
		.canvasformHeaderWrapper {
			margin-left: 0 !important;
		}
		.canvasFormLeftNavSlider {
			margin-top: 0 !important;
		}
		.canvasFormLeftNavSliderSubList {
			margin-left: var(--px-10) !important;
		}
		.section-validationCount .validationcount {
			margin: 0 var(--px-10) 0 0 !important;
		}
		.section-validationCount .motif-dropdown-portal .ValidationIcon {
			padding-right: var(--px-12) !important;
			@media only screen and (max-width: 1366px) {
				padding-right: var(--px-20) !important;
			}
		}
		.canvasFormLeftNavSearch {
			padding-left: var(--px-20) !important;
			padding-right: var(--px-20) !important;
			padding-bottom: var(--px-16) !important;
			.canvasFormLeftNavContainer {
				.canvasFormName {
					justify-content: center;
				}
			}
			.canvasFormLeftNavControls {
				.canvasFormLeftNavMoreMenu {
					width: 100%;
					justify-content: center;
				}
				&.canvasFormLeftNavControlsViewTypeSelect {
					justify-content: center;
					border-bottom: var(--px-1) solid var(--neutrals-100) !important;
					border-top: var(--px-1) solid var(--neutrals-100) !important;
					.view-type-icon-container {
						padding-left: 0;
						padding-right: 0;
						width: 100%;
						justify-content: center;
						&:first-child {
							padding-top: var(--px-16);
						}
						&:last-child {
							padding-bottom: 0 !important;
						}
					}
				}
				&.canvasFormLeftNavControlsFilter {
					display: none !important;
				}
				.validationControls {
					padding-bottom: var(--px-16) !important;
					margin-left: 0 !important;
					width: 100% !important;
					.StyledMotifDropdownPortal {
						width: 100% !important;
						display: flex;
						justify-content: center;
						margin-left: 0 !important;
						.ValidationIcon {
							justify-content: center !important;
							.validationcount {
								order: 1 !important;
								font-size: var(--px-12) !important;
								font-weight: 400 !important;
								line-height: var(--px-18) !important;
							}
							.ValidationImg {
								order: 2 !important;
								.ValidationImgIcon {
									svg {
										width: var(--px-18) !important;
										height: var(--px-18) !important;
									}
								}
							}
						}
					}
				}
			}
		}
		.headerSection {
			padding-left: calc(50% - var(--px-10)) !important;
			.canvasFormLeftNavSlider {
				ul:before {
					top: var(--px-22) !important;
				}
				.canvasformHeader {
					.canvasformHeaderIcon {
						margin-left: var(--px-1) !important;
					}
					.header-validationCount {
						width: auto !important;
						margin-left: 0 !important;
						margin-right: 0 !important;
						.ValidationIcon {
							justify-content: flex-start !important;
							.validationcount {
								margin-left: var(--px-12) !important;
							}
						}
					}
				}
				.canvasformSubHeader {
					.canvasformSubHeaderIcon {
						margin-right: 0;
					}
					.section-validationCount {
						width: auto !important;
						margin-left: 0 !important;
						margin-right: 0 !important;
						.ValidationIcon {
							justify-content: flex-start !important;
							.validationcount {
								margin-left: var(--px-15) !important;
							}
						}
					}
				}
			}
		}
	}
	&.leftSideSectionExpanded {
		.canvasFormLeftNavWrapper {
			.canvasFormLeftNavSearch {
				.canvasFormLeftNavContainer {
					.canvasFormName {
						.canvasFormLeftNavMenuLink {
							margin-left: auto;
						}
					}
				}
			}
			.canvasformSubHeader {
				.canvasformSubHeaderIcon {
					margin-right: var(--px-8) !important;
				}
			}
			.canvasformHeaderWrapper {
				margin-left: var(--px-2) !important;
			}
			.canvasFormLeftNavSliderSubList {
				margin-left: var(--px-10) !important;
			}
		}
	}
	.ClearCommentsLoader {
		z-index: 10 !important;
		background-color: var(--neutrals-900);
		opacity: 0.4;
	}
	.inlineView {
		display: flex;
		height: 100% !important;
		align-items: center !important;
		position: absolute !important;
		z-index: 99;
	}
	.RailOverlay {
		background: var(--neutrals-900);
		opacity: 0.4;
		position: absolute !important;
		width: 100%;
		height: 100%;
		left: 0;
		z-index: 10 !important;
		&.LoaderOverlay {
			z-index: 99 !important;
		}
		.Rail-loading {
			position: absolute !important;
			top: 50%;
			z-index: 100;
			opacity: 1;
		}
	}
	.canvasFormLeftNavWrapper {
		display: flex;
		flex-direction: column;
		width: var(--px-350);
		transition: 0.5s;
		transition-property: width;
		height: 100%;
		.StyledPlayer {
			input[type='range'] {
				-webkit-appearance: none;
			}
		}
		.navigationUnavailable {
			font-size: var(--px-14);
			color: var(--neutrals-900);
			transition: 0.5s;
			transition-property: width;
			padding: var(--px-10) var(--px-10) var(--px-10) 0;
			height: 100%;
		}
		.canvasFormLeftNavSearch {
			display: block;
			padding: var(--px-10) var(--px-20) var(--px-10) 0;
			.canvasFormLeftNavContainer {
				display: flex;
				flex-direction: column;
				width: 100%;
				.canvasFormBackContainer {
					display: flex;
					width: 100%;
				}
				.canvasFormBack {
					display: flex;
					width: auto;
					max-width: var(--px-50);
					color: var(--blue-600);
					font-size: var(--px-12);
					cursor: pointer;
					font-weight: 400;
					font-size: var(--px-14);
					line-height: var(--px-20);
					&:hover {
						text-decoration: underline;
					}
				}
				.contentUpdateAvailable {
					margin-left: auto;
					display: flex;
					font-size: var(--px-12);
					width: auto;
					max-width: var(--px-250);
					a {
						display: flex;
						color: var(--blue-600);
						align-items: center;
						max-width: 100%;
						font-size: var(--px-14);
						text-decoration: none;
						font-weight: 400;
						line-height: var(--px-20);
						.motif-icon {
							margin-right: var(--px-8);
							height: var(--px-20);
							svg {
								width: var(--px-20) !important;
								height: var(--px-20) !important;
								fill: var(--blue-600);
							}
						}
					}
				}
				.canvasFormName {
					display: flex;
					width: 100%;
					padding-top: var(--px-16);
					padding-bottom: var(--px-16);
					.canvasFormDocName {
						display: inline-block;
						width: var(--px-300);
						color: var(--neutrals-900);
						font-size: var(--px-18);
						font-weight: 400;
						cursor: default;
						line-height: normal;
						& > span {
							width: 100%;
							display: inline-flex;
						}
					}
					.canvasFormLeftNavMenuLink {
						display: inline-flex;
						justify-content: flex-start;
						align-items: flex-start;
						cursor: pointer;
						width: var(--px-24);
						svg {
							fill: var(--neutrals-900);
							color: var(--neutrals-900);
							width: var(--px-24);
							height: var(--px-24);
							&.medium {
								max-height: 100%;
								height: var(--px-24);
								max-width: 100%;
								width: var(--px-20);
							}
						}
					}
				}
			}
			.canvasFormLeftNavControls {
				display: flex;
				width: 100%;
				padding-top: 0;
				padding-bottom: var(--px-16);
				align-items: center;
				gap: var(--px-4);
				.canvasFormLeftNavPrint {
					margin-left: auto !important;
					.IconPrint {
						width: var(--px-20);
						height: var(--px-20);
						cursor: pointer;
						.motion-icon {
							cursor: pointer;
						}
					}
				}
				&.whiteBottomBorder {
					border-bottom: var(--px-1) solid var(--neutrals-100);
				}
				&.canvasFormLeftNavControlsViewTypeSelect {
					.canvasFormLeftNavSelect {
						width: calc(100% - var(--px-90));
					}
				}
				.StyledNotesCount {
					&.notesControl {
						.NotesIconsImg {
							svg {
								height: var(--px-20);
								width: var(--px-20);
							}
						}
						.NotesCountLabels {
							margin: 0 !important;
							padding: 0 !important;
							.motif-badge {
								outline: none !important;
								padding: 0 !important;
								background-color: transparent !important;
								.motif-badge-number {
									padding-left: 0;
									padding-top: 0;
									font-size: var(--px-14);
									font-weight: 400;
									line-height: var(--px-20);
								}
							}
						}
					}
				}
				.canvasFormLeftNavSelectTxt,
				.canvasFormLeftViewSection {
					color: var(--neutrals-900);
					font-size: var(--px-14);
					width: var(--px-50);
				}
				.canvasFormLeftNavSelect {
					display: inline-flex;
					width: calc(100% - var(--px-40));
					.inCompleteSelect,
					.formViewTypeSelect {
						.react-select-container {
							.react-select__control {
								background: transparent;
								border-bottom: var(--px-1) solid transparent;
								.selectText {
									color: var(--neutrals-900);
									vertical-align: middle;
									margin-top: var(--px-2);
								}
								.react-select__value-container {
									color: var(--neutrals-900);
									height: auto;
									&.react-select__value-container--has-value {
										.react-select__input {
											padding-top: 0;
										}
									}
								}
								.react-select__single-value {
									padding-top: var(--px-8);
									.IconandLabelWrapper {
										display: flex;
										align-items: center;
										.IconName {
											svg {
												vertical-align: middle;
												fill: var(--neutrals-00white);
											}
										}
										.IconLabel {
											color: var(--neutrals-00white);
											padding-left: var(--px-10);
										}
									}
								}
							}
							.react-select__menu {
								z-index: 99;
								.react-select__menu-list {
									.react-select__option--is-focused {
										background-color: var(--neutrals-200);
									}
									.react-select__option {
										.IconandLabelWrapper {
											display: flex;
											align-items: center;
											.IconName {
												svg {
													vertical-align: middle;
													fill: var(--neutrals-900);
												}
											}
											.IconLabel {
												color: var(--neutrals-900);
												padding-left: var(--px-10);
											}
										}
									}
								}
							}
							.selectText {
								display: inline-block;
								width: 100%;
								line-height: normal;
								text-overflow: ellipsis;
								overflow: hidden;
								white-space: nowrap;
							}
						}
						&.floating-label {
							.react-select__single-value {
								padding-top: 0 !important;
							}
						}
					}
				}
				.canvasFormLeftNavForms {
					margin-left: 0;
					display: inline-flex;
					position: relative;
					.motif-icon {
						svg {
							width: var(--px-30) !important;
							height: var(--px-30) !important;
							fill: var(--neutrals-900);
							path {
								fill: var(--neutrals-900);
							}
						}
					}
					.motif-dropdown {
						.motif-dropdown-trigger {
							.motif-icon {
								svg {
									width: var(--px-30) !important;
									height: var(--px-30) !important;
									fill: var(--neutrals-00white);
								}
							}
						}
					}
					&.canvasFormLeftNavFormsMotifDropdown {
						.motif-dropdown-trigger {
							svg {
								width: var(--px-30) !important;
								height: var(--px-30) !important;
								max-width: 100% !important;
								max-height: 100% !important;
								fill: var(--neutrals-00white);
							}
						}
						.motif-dropdown-menu {
							.IconandLabelWrapper {
								display: flex;
								align-items: center;
								.IconName {
									svg {
										vertical-align: middle;
										fill: var(--neutrals-900);
									}
								}
								.IconLabel {
									color: var(--neutrals-900);
									padding-left: var(--px-10);
								}
							}
						}
					}
				}
				.independeStatus {
					display: flex;
					flex-direction: column;
					width: 100%;
					.statusLabel,
					.reasonLabel {
						color: var(--neutrals-00white);
						display: block;
						font-size: var(--px-12);
						font-weight: bold;
						text-decoration: underline;
					}
					.statusDescription {
						color: var(--neutrals-200);
						display: block;
						font-size: var(--px-12);
						font-weight: bold;
					}
					.modifiedBy {
						color: var(--neutrals-200);
						display: inline-block;
						font-size: var(--px-12);
					}
					.independenceModifyDate {
						color: var(--neutrals-200);
						display: inline-block;
						margin-left: var(--px-5);
					}
					.reasonDescription {
						color: var(--neutrals-200);
						display: block;
						font-size: var(--px-12);
						font-weight: 300;
						width: 100%;
						cursor: default;
					}
				}
				.canvasFormControls {
					display: flex;
					width: auto;
					max-width: calc(100% - var(--px-100));
					align-items: center;
					.signOffControls {
						display: inline-flex;
						.signOffContainer {
							.signOffButton {
								.btnLabel {
									color: var(--neutrals-900);
									width: var(--px-30);
								}
								svg {
									fill: var(--neutrals-900);
									path {
										fill: var(--neutrals-900);
									}
								}
							}
							.signOffContent {
								.signOffItem {
									button {
										top: var(--px-5);
										@media only screen and (max-width: 1280px) {
											top: var(--px-9);
										}
									}
								}
							}
						}
					}
					.notesControl {
						.btn-default {
							margin: 0;
							.NotesCountLabels {
								font-size: var(--px-14) !important;
								font-weight: 400 !important;
							}
						}
					}
				}
				.validationControls {
					display: inline-flex;
					width: var(--px-60);
					margin-left: var(--px-10);
					.StyledMotifDropdownPortal {
						margin-left: var(--px-8);
					}
					.ValidationIcon {
						margin: 0;
						fill: var(--neutrals-00white);
						padding: 0 var(--px-5);
						border-radius: var(--px-5);
						color: var(--neutrals-00white);
						cursor: pointer;
						font-size: var(--px-12);
						font-weight: 600;
						justify-content: flex-start !important;
						svg {
							margin: calc(var(--px-2) * -1) 0 0 0;
							vertical-align: middle;
							color: var(--neutrals-900);
							width: var(--px-20) !important;
							height: var(--px-20) !important;
						}
						.validationcount {
							color: var(--red-600) !important;
							font-size: var(--px-14) !important;
							font-weight: 400 !important;
							line-height: var(--px-20) !important;
							order: 2; // To swap the location of the validation count and icon
						}
						.ValidationImg {
							order: 1;
							.ValidationImgIcon {
								svg {
									color: var(--red-600) !important;
									width: var(--px-16) !important;
									height: var(--px-16) !important;
								}
							}
						}
					}
				}
				.canvasFormLeftNavMoreMenu {
					display: inline-flex;
					margin-left: auto;
					.motif-dropdown {
						.motif-button {
							padding: 0;
							.motif-icon {
								svg {
									color: var(--neutrals-900);
									path {
										fill: var(--neutrals-900);
									}
								}
							}
						}
						.motif-dropdown-menu {
							left: calc(var(--px-5) * -1);
							top: var(--px-35) !important;
							@media only screen and (max-width: 1280px) {
								left: calc(var(--px-20) * -1);
							}
						}
					}
					.motif-icon {
						svg {
							width: var(--px-20) !important;
							height: var(--px-20) !important;
							padding: 0;
							path {
								fill: var(--neutrals-00white);
							}
						}
					}
				}
				&.canvasFormLeftNavControlsBlock {
					display: block;
					.validationControls {
						margin-bottom: var(--px-5);
						.ValidationIcon {
							padding: 0;
							justify-content: flex-start !important;
							.validationcount {
								padding-left: 0;
							}
						}
					}
				}
			}
			.canvasFormLeftSearch {
				.railSearchWrapper {
					margin: var(--px-16) 0 var(--px-10) 0;
					width: 100%;
					background: transparent;
					display: flex;
				}
			}
		}
		.canvasFormLeftNavSlider {
			transition: 0.5s;
			transition-property: width;
			margin-top: var(--px-10);
			max-height: calc(100% - var(--px-6));
			@media only screen and (max-width: 1280px) {
				max-height: calc(100% - var(--px-10));
			}
			ul.canvasFormLeftNavSliderList {
				.canvasformHeader {
					margin-left: 0;
					justify-content: center;
					padding-top: var(--px-3);
					margin-bottom: var(--px-15);
					.canvasformHeaderWrapper {
						margin-left: var(--px-5);
						display: flex;
						align-items: flex-start;
						width: 100%;
					}
					.canvasformHeaderIcon {
						border: var(--px-1) solid var(--neutrals-700);
						background-color: var(--neutrals-700);
						margin: 0 0 0 0;
						width: var(--px-18);
						height: var(--px-18);
						display: inline-flex;
						margin-right: var(--px-16);
						margin-top: var(--px-1);
						border-radius: var(--px-2);
					}
					.canvasformHeaderText {
						width: var(--px-184);
						min-height: var(--px-16);
						font-size: var(--px-14);
						font-weight: 400;
						display: inline-flex;
						color: var(--neutrals-900);
						line-height: var(--px-20);
						margin: 0 var(--px-20) 0 0;
					}
					.canvasformCustomHeader {
						height: var(--px-24);
						.motif-dropdown {
							margin: 0;
							.motif-text-button {
								padding-top: 2px;
							}
							svg {
								margin-top: calc(var(--px-2) * -1);
								fill: var(--neutrals-900);
								path {
									fill: var(--neutrals-900);
								}
							}
						}
					}
					.header-validationCount {
						display: inline-flex;
						width: var(--px-45);
						height: var(--px-16);
						align-items: flex-start;
						margin: 0 var(--px-12) 0 auto;
						@media only screen and (max-width: 1280px) {
							height: var(--px-17);
							width: var(--px-37);
						}
						.validationcount {
							max-width: var(--px-40);
							color: var(--red-600);
							font-size: var(--px-12);
							font-weight: 400;
							padding-left: 0;
						}
						.ValidationImg {
							.ValidationImgIcon {
								svg {
									color: var(--red-600);
									width: var(--px-16) !important;
									height: var(--px-16) !important;
								}
							}
						}
					}
					.validationControls {
						.motif-dropdown-menu {
							z-index: 9;
						}
					}
				}
			}

			ul.canvasFormLeftNavSliderList,
			ul.canvasFormLeftNavSliderList ul {
				list-style-type: none;
				padding-bottom: var(--px-5);
			}

			ul.canvasFormLeftNavSliderList ul {
				margin-left: var(--px-13);
				&:before {
					content: '';
					display: block;
					position: absolute;
					top: var(--px-24);
					bottom: 0;
					border-left: var(--px-1) solid var(--neutrals-100);
				}
				&:last-child {
					padding-bottom: 0;
				}
			}

			ul.canvasFormLeftNavSliderList li {
				position: relative;
				margin: var(--px-5) 0 0 calc(var(--px-3) * -1);
				padding: var(--px-3) var(--px-12) 0 0;
				cursor: pointer;
				line-height: normal;

				&.canvasformHeader {
					padding-left: 0;
					&:first-child {
						margin-top: 0;
					}
				}

				&.activeHeader {
					.canvasformHeaderIcon {
						background-color: var(--neutrals-900);
						border: var(--px-1) solid var(--neutrals-900);
					}
					.canvasformHeaderText {
						color: var(--neutrals-900);
						font-weight: 400;
					}
				}

				&.activeSection {
					.canvasformSubHeaderIcon {
						background-color: var(--teal-600) !important;
						border: var(--px-1) solid var(--teal-600);
					}
					.canvasformSubHeaderText {
						color: var(--teal-600);
					}
					.section-validationCount {
						.validationcount {
							color: var(--red-600);
						}
						.ValidationImg {
							.ValidationImgIcon {
								svg {
									color: var(--red-600);
									width: var(--px-16) !important;
									height: var(--px-16) !important;
								}
							}
						}
					}
				}
				.canvasformSectionWrapper {
					display: flex;
					align-items: flex-start;
					width: 100%;
				}

				.canvasformSubHeaderIcon {
					border-radius: var(--px-10);
					border: var(--px-1) solid var(--neutrals-700);
					margin: var(--px-8) var(--px-8) 0 calc(var(--px-2) * -1);
					width: var(--px-12);
					height: var(--px-12);
					display: inline-flex;
					vertical-align: top;
					z-index: 9;
					background-color: var(--neutrals-700);
					@media only screen and (max-width: 1280px) {
						margin: var(--px-10) 0 0 calc(var(--px-2) * -1);
					}
				}
				.canvasformSubHeaderText {
					display: inline-flex;
					width: var(--px-216);
					font-size: var(--px-12);
					font-weight: 400;
					color: var(--neutrals-900);
					margin: var(--px-7) var(--px-15) 0 var(--px-10);
					@media only screen and (max-width: 1280px) {
						margin: var(--px-10) var(--px-15) 0 var(--px-10);
						width: var(--px-250);
					}
					vertical-align: top;
					line-height: normal;
					word-break: break-all;
				}
			}
			.canvasformSubHeader {
				padding-right: 0 !important;
				.section-validationCount {
					display: inline-flex;
					width: var(--px-40);
					align-items: flex-start;
					height: var(--px-15);
					margin: var(--px-10) var(--px-10) 0 var(--px-10);
					@media only screen and (max-width: 1280px) {
						height: var(--px-14);
					}
					.validationcount {
						max-width: var(--px-40);
						color: var(--red-600);
						font-size: var(--px-12);
						font-weight: 400;
						margin: 0 var(--px-4);
						padding: 0;
					}
					.ValidationImg {
						.ValidationImgIcon {
							svg {
								color: var(--red-600);
								width: var(--px-16) !important;
								height: var(--px-16) !important;
							}
						}
					}
					.motif-tooltip-wrapper {
						width: 100%;
						.motif-tooltip-trigger-wrap {
							.motif-button {
								width: 100%;
							}
						}
					}
				}
			}

			.canvasformSubHeader {
				display: flex;
				justify-content: flex-start;
				padding: 0;
				margin-left: calc(var(--px-4) * -1);
				cursor: default;
				.canvasformSubHeaderIcon {
					cursor: pointer;
					z-index: 9;
					background-color: var(--neutrals-900);
					margin-right: 0;
					&:before {
						display: none;
					}
				}
				.canvasformSubHeaderText {
					display: none;
				}
			}

			ul.canvasFormLeftNavSliderList ul li:before {
				content: '';
				display: block;
				position: absolute;
				left: 0;
				width: var(--px-12);
				@media only screen and (max-width: 1280px) {
					top: var(--px-16);
				}
			}

			ul.canvasFormLeftNavSliderList ul li:last-child:before {
				top: var(--px-15);
				bottom: 0;
				height: auto;
				@media only screen and (max-width: 1280px) {
					top: var(--px-16);
				}
			}

			.emptyContainer {
				color: var(--neutrals-900);
				display: flex;
				font-size: var(--px-16);
				justify-content: center;
				&.hideContent {
					display: none;
				}
			}
			.noresult {
				height: 100%;
				align-items: center;
			}
		}
		.inCompleteSection {
			width: calc(100% - var(--px-20));
			&.navigationBorder {
				border-top: var(--px-1) solid var(--neutrals-300);
				margin-bottom: var(--px-10);
				margin-top: var(--px-10);
			}
			.incomplete-section-checkbox {
				color: var(--neutrals-00white);
				display: block;
				margin: var(--px-20) var(--px-20) var(--px-10) 0;
				.motif-checkbox {
					display: inline-flex;
					width: auto;
					.motif-checkbox-label {
						width: 90%;
					}
				}
			}
			.submitProfileBtn {
				margin: var(--px-10) var(--px-20) 0 var(--px-20);
				width: calc(100% - var(--px-40));
				position: relative;
				display: flex;
				align-items: center;
				.returnToDashboardLink {
					color: var(--blue-600);
					width: 100%;
					font-size: var(--px-14);
					font-weight: bold;
					cursor: pointer;
					&:hover {
						text-decoration: underline;
					}
					.ellipses {
						justify-content: center;
					}
				}
				.motif-button-secondary {
					margin-bottom: var(--px-15);
					width: 100%;
					&:disabled:hover + .disabled-infotooltip {
						display: block;
					}
				}

				.disabled-infotooltip {
					position: absolute;
					z-index: 99999;
					background: var(--neutrals-00white);
					color: var(--neutrals-900);
					left: 0;
					width: var(--px-300);
					bottom: var(--px-55);
					padding: var(--px-13);
					line-height: var(--px-16);
					display: none;
					font-size: var(--px-12);

					& .tooltip-downarrow {
						&:before {
							content: '';
							display: block;
							position: absolute;
							left: 50%;
							top: 100%;
							width: 0;
							height: 0;
							border: var(--px-10) solid transparent;
							border-top-color: var(--neutrals-00white);
						}
					}
				}
			}
			.inCompleteSectionBtn,
			.accountDetailsSectionBtn {
				display: flex;
				margin: var(--px-10) 0 0 0;
				&.radioBtnList {
					display: block;
				}
				.inCompleteViewHeader {
					font-size: var(--px-14);
					font-weight: bold;
					color: var(--neutrals-00white);
					margin: 0 0 var(--px-10) 0;
				}
				.motif-button-primary-alt {
					margin-bottom: var(--px-15);
					width: 100%;
				}
			}
			.CCPWrapper {
				display: flex;
				flex-direction: column;
				width: calc(100% - var(--px-40));
				margin: 0 var(--px-20);
				.CCPContent {
					display: flex;
					color: var(--neutrals-00white);
					font-size: var(--px-12);
					font-weight: normal;
					line-height: var(--px-18);
					margin: var(--px-10) 0 var(--px-10) 0;
					.motif-icon {
						margin-right: var(--px-10);
						svg {
							width: var(--px-24) !important;
							height: var(--px-24) !important;
							max-width: var(--px-24) !important;
							max-height: var(--px-24) !important;
							fill: var(--blue-600);
						}
					}
				}
				.BackToCCPBtn {
					display: flex;
					margin: 0 0 var(--px-10) 0;
					svg {
						fill: var(--blue-600);
						width: var(--px-20) !important;
						height: var(--px-20) !important;
						max-width: var(--px-20) !important;
						max-height: var(--px-20) !important;
						margin-top: var(--px-3);
						@media only screen and (max-width: 1280px) {
							margin-top: var(--px-5);
						}
					}
					.returnToDashboardLink {
						color: var(--blue-600);
						width: 100%;
						font-size: var(--px-14);
						font-weight: bold;
						cursor: pointer;
						margin-left: var(--px-5);
						width: calc(100% - var(--px-20));
						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
		}
	}
	.hideCanvasFormBorder {
		width: var(--px-116);
		@media only screen and (max-width: 1280px) {
			width: var(--px-100);
		}
		.canvasFormLeftNavSearch {
			padding-right: var(--px-10);
		}
		.navigationUnavailable {
			display: none;
		}
		.canvasFormLeftNavControlsFilter {
			display: none;
		}
		.canvasFormLeftNavSearch {
			.canvasFormLeftNavContainer {
				.canvasFormDocName,
				.canvasFormLeftNavMoreMenu {
					display: none !important;
				}
				.canvasFormBackContainer {
					display: none !important;
				}
				.canvasFormLeftNavMenuLink {
					justify-content: flex-start;
					width: 100%;
				}
			}
			.canvasFormLeftNavControls {
				.canvasFormControls {
					display: none;
				}
				&.whiteBottomBorder {
					display: block;
					border: 0;
				}
				.canvasFormLeftNavForms {
					display: inline-flex;
					position: relative;
					top: var(--px-5);
					border: var(--px-1) solid #fff;
					border-width: var(--px-1) 0;
					padding: var(--px-10) 0;
				}
				.independeStatus {
					display: none;
				}
			}
			.canvasFormLeftSearch {
				display: none;
			}
		}
		.canvasFormLeftNavSlider {
			ul.canvasFormLeftNavSliderList {
				margin-left: 0;
				ul li:before {
					display: none;
				}
				.canvasformHeader {
					margin-left: 0;
					justify-content: center;
					padding-top: var(--px-3);
					margin-bottom: var(--px-15);
					cursor: default;
					padding-left: 0;
					.canvasformHeaderWrapper {
						display: flex;
						justify-content: flex-start;
						padding: 0 0 var(--px-10) 0;
					}
					.canvasformHeaderIcon {
						margin: 0;
						cursor: pointer;
					}
					.canvasformHeaderText {
						display: none;
					}
					.canvasformCustomHeader {
						max-width: var(--px-20);
						margin-top: calc(var(--px-4) * -1);
					}
					.header-validationCount {
						height: var(--px-20);
						margin-top: calc(var(--px-2) * -1);
						width: var(--px-40);
						@media only screen and (max-width: 1280px) {
							margin-left: var(--px-7);
							margin-top: calc(var(--px-5) * -1);
						}
						.motif-tooltip-wrapper {
							width: 100%;
							.motif-tooltip-trigger-wrap {
								.motif-button {
									width: 100%;
								}
							}
						}
					}
					.ValidationIcon {
						.ValidationImg {
							display: none;
							&.hide {
								display: none;
							}
						}
					}
				}
				.canvasFormLeftNavSliderSubList {
					margin-left: var(--px-15);
					.canvasformSubHeader {
						display: flex;
						justify-content: flex-start;
						padding: 0;
						margin-left: calc(var(--px-4) * -1);
						cursor: default;
						.canvasformSubHeaderIcon {
							&:before {
								display: none;
							}
						}
						.canvasformSubHeaderText {
							display: none;
						}
						.section-validationCount {
							height: var(--px-12);
							width: var(--px-35);
							margin-top: var(--px-4);
						}
					}
				}
			}
		}
		.headerSection {
			height: calc(100% - var(--px-100));
			overflow-y: overlay;
		}
	}
	.headerSection {
		height: calc(100% - var(--px-180));
		overflow-x: hidden;
		@media only screen and (max-width: 1280px) {
			height: calc(100% - var(--px-200));
		}
	}
	&.leftSideSectionCollapsed {
		.headerSection {
			.canvasFormLeftNavSlider {
				.canvasFormLeftNavSliderList {
					width: var(--px-100);
					.canvasformHeader {
						.canvasformHeaderWrapper {
							.header-validationCount {
								@media only screen and (max-width: 1280px) {
									margin-left: var(--px-10);
									.ValidationIcon {
										padding-right: 0;
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;
